<template>
  <q-table
    title="User Group Logs"
    :columns="columnDefsLast"
    :rows="rowDataLast"
    row-key="uid"
    :filter="filterLast"
    :filter-method="customFilterLast"
    :loading="loadingLast"
    :pagination="paginationLast"
    :sort-method="CommonAdminHelper.customSort"
    virtual-scroll
    wrap-cells
    class="tableClass"
  >
    <template v-slot:top>
      <q-input
        outlined
        dense
        style="width: 10%"
        label="Filter User Group Logs"
        debounce="300"
        v-model="filterLast"
      />
    </template>

    <template v-slot:header="props">
      <q-tr :props="props">
        <q-th auto-width />
        <q-th v-for="col in props.cols" :key="col.name" :props="props">
          {{ col.label }}
        </q-th>
      </q-tr>
    </template>

    <template v-slot:body="props">
      <q-tr :props="props">
        <q-td auto-width>
          <q-btn size="sm" color="accent" round dense @click="props.expand = !props.expand" :icon="props.expand ? 'remove' : 'add'"
            :title="`${props.expand ? 'Collapse' : 'Expand'}`" :aria-label="`${props.expand ? 'Collapse' : 'Expand'}`" />
        </q-td>
        <q-td v-for="col in props.cols" :key="col.name" :props="props">
          {{ col.value }}
        </q-td>
      </q-tr>
      <q-tr v-show="props.expand" :props="props">
        <q-td colspan="90%">
          <q-table
            :columns="columnDefsLogs"
            :rows="getRowDataLogs(props.row.uid)"
            :pagination="paginationLogs"
            :sort-method="CommonAdminHelper.customSort"
            virtual-scroll
            wrap-cells
            class="tableClassLogs"
          >
            <template v-slot:body="props">
              <q-tr :props="props">
                <q-td v-for="col in props.cols" :key="col.name" :props="props">
                  <div v-if="col.name === 'changes'">
                    <div v-for="change in col.value" :key="change">
                      {{ change }}
                    </div>
                  </div>
                  <div v-else>{{ col.value }}</div>
                </q-td>
              </q-tr>
            </template>
          </q-table>
        </q-td>
      </q-tr>
    </template>
  </q-table>
</template>

<script>
import { computed, ref, watch } from "vue";
import CommonAdminHelper from "../helper/CommonAdminHelper.js";
import NotificationHelper from "../helper/NotificationHelper";
import EmailService from "../service/EmailService.js";
import LogService from "../service/LogService.js";
import moment from "moment-timezone";

export default {
  name: "LogUserGroupTable",
  props: {
    currentUserUID: String
  },
  setup(props) {
    const columnDefsLast = ref([
      { label: "Name", name: "name", field: "name", align: "center", sortable: true },
      { label: "Last Changed By", name: "last_changed_by", field: "last_changed_by", align: "center", sortable: true },
      { label: "Last Changed Date", name: "last_changed_date", field: "last_changed_date", align: "center", sortable: true }
    ]);

    const columnDefsLogs = ref([
      { label: "Type", name: "type", field: "type", align: "center", sortable: true },
      { label: "Changed By", name: "changed_by", field: "changed_by", align: "center", sortable: true },
      { label: "Changed Date", name: "changed_date", field: "changed_date", align: "center", sortable: true },
      { label: "Changes", name: "changes", field: "changes", align: "center" }
    ]);

    const logsByUserGroup = ref({});

    const rowDataLast = computed(() => {
      let returnValue = [];

      Object.keys(logsByUserGroup.value).forEach((uid) => {
        let logs = logsByUserGroup.value[uid];

        let name = logs[0].record_name;
        let last_changed_by = `${logs[0].changed_by_name} (${logs[0].changed_by_email_address})`;

        if (logs[0].changed_by_ip_address !== undefined && logs[0].changed_by_ip_address !== "") {
          last_changed_by = `${last_changed_by} - ${logs[0].changed_by_ip_address}`;
        }

        let last_changed_date = CommonAdminHelper.convertTimeToHST(logs[0].date);

        let logRow = {
          uid: uid,
          name: name,
          last_changed_by: last_changed_by,
          last_changed_date: last_changed_date,
        }

        logs.forEach((log, index) => {
          Object.keys(log).forEach((key) => {
            if (key !== "changed_by_uid" && key !== "date" && key !== "record_uid" && key !== "type") {
              logRow[`${key}_${index}`] = log[key];
            }
          })
        })

        returnValue.push(logRow);
      })

      return returnValue;
    });

    const filterLast = ref("");
    const loadingLast = ref(false);
    const paginationLast = ref({ rowsPerPage: 10, sortBy: "last_changed_date", descending: true });
    const paginationLogs = ref({ rowsPerPage: 10, sortBy: "changed_date", descending: true });

    watch(
      () => props.currentUserUID,
      () => {
        if (props.currentUserUID !== null && props.currentUserUID !== undefined) {
          getLogUserGroup();
        }
      },
      { deep: true, immediate: true }
    );

    async function getLogUserGroup() {
      try {
        loadingLast.value = true;

        const response = await LogService.getLogUserGroup(props.currentUserUID);

        response.data.sort((a, b) => {
          // sort by latest log first
          const dateA = moment(a.date).toDate();
          const dateB = moment(b.date).toDate();

          return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
        }).forEach((log) => {
          if (logsByUserGroup.value[log.record_uid] === undefined) {
            logsByUserGroup.value[log.record_uid] = [];
          }

          logsByUserGroup.value[log.record_uid].push(log)
        })

        loadingLast.value = false;
      } catch (error) {
        EmailService.sendErrorEmail("LogUserGroupTable.vue - getLogUserGroup", error);
        NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        loadingLast.value = false;
      }
    }

    function customFilterLast(rows) {
      return CommonAdminHelper.customFilter(rows, filterLast, null);
    }

    function getRowDataLogs(uid) {
      let returnValue = [];

      let logs = logsByUserGroup.value[uid];

      logs.forEach((log) => {
        let type = log.type;
        let changed_by = `${log.changed_by_name} (${log.changed_by_email_address})`;

        if (log.changed_by_ip_address !== undefined && log.changed_by_ip_address !== "") {
          changed_by = `${changed_by} - ${log.changed_by_ip_address}`;
        }

        let changed_date = CommonAdminHelper.convertTimeToHST(log.date);
        let changes = CommonAdminHelper.getLogChanges(log);

        let logRow = {
          type: type,
          changed_by: changed_by,
          changed_date: changed_date,
          changes: changes
        }

        returnValue.push(logRow);
      })

      return returnValue;
    }

    return {
      //helper
      CommonAdminHelper,

      //variable properties
      columnDefsLast,
      columnDefsLogs,
      rowDataLast,
      filterLast,
      loadingLast,
      paginationLast,
      paginationLogs,

      //functions
      customFilterLast,
      getRowDataLogs
    };
  },
};
</script>